import React, { useMemo, useState } from 'react';
import { Form, Select } from 'antd';
import { useGetSelectFieldValues } from '@/shared/api/client/field-configuration-controller';
import type { SelectFieldOption } from '@/shared/api/model';
import type { ReportParameter } from '../../types';

export interface GlProductSelectProps {
  param: ReportParameter;
  disabled?: boolean;
  placeholder?: string;
  form: any;
  reportCode?: string;
  reportCategory?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  onBlur?: () => void;
}

/**
 * GL Product select component for choosing savings/loan products by GL number.
 * Fetches from the 'products' provider in the select-field-values API.
 *
 * Used for parameter type: GL_SELECT
 */
const GlProductSelect: React.FC<GlProductSelectProps> = ({
  param,
  disabled,
  placeholder,
  form,
  reportCode,
  reportCategory,
  value,
  onChange,
  onBlur,
}) => {
  const [search, setSearch] = useState<string | undefined>(undefined);

  const branchValue = Form.useWatch('branch', form);

  const params = useMemo(() => {
    const p: Record<string, unknown> = {
      search,
      fieldName: param.name,
      entityType: 'REPORT',
      reportCode,
      reportCategory,
    };

    if (branchValue) {
      p.branch = branchValue;
    }

    return p;
  }, [search, param.name, branchValue, reportCode, reportCategory]);

  const {
    data: options,
    isLoading,
    error,
  } = useGetSelectFieldValues('products', params as any, {
    query: {
      staleTime: 5 * 60 * 1000,
    },
  });

  const selectOptions = useMemo(() => {
    const list: SelectFieldOption[] = Array.isArray(options) ? options : [];
    const mapped = list.map((opt) => ({
      value: opt.value,
      label: opt.label,
      disabled: opt.disabled,
    }));

    const hasAll = mapped.some((o) => String(o.value) === '-1');
    if (!hasAll && (param.defaultValue === -1 || param.defaultValue === '-1')) {
      mapped.unshift({ value: '-1', label: 'All Products', disabled: undefined });
    }

    return mapped;
  }, [options, param.defaultValue]);

  return (
    <Select
      placeholder={placeholder || 'Select product'}
      disabled={disabled}
      loading={isLoading}
      showSearch
      allowClear
      filterOption={false}
      onSearch={(q) => setSearch(q)}
      options={selectOptions}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      notFoundContent={
        error ? 'Failed to load products' : isLoading ? 'Loading...' : 'No products'
      }
      optionFilterProp="label"
    />
  );
};

export default GlProductSelect;
